/**
 * Auth Interceptor — добавление JWT-токена и обработка 401 с refresh.
 *
 * Важно: запрос refresh идёт через ОТДЕЛЬНЫЙ «голый» axios без интерсепторов.
 * Иначе 401 самого refresh снова попадал бы в этот же обработчик и порождал
 * бесконечный цикл refresh → 401 → refresh (из-за чего страница долбила
 * POST /auth/refresh и не уходила на логин).
 */
import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosError } from 'axios'
import type { AuthTokens, RefreshTokenResponse } from '@/features/auth/types/auth.types'
import { isImpersonating, tokenStorage } from '@/features/auth/tokenStorage'
import { useToast } from '@/design-system/composables/useToast'
import { i18n } from '@/i18n'

// Extend AxiosRequestConfig type для поддержки _retry флага
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean
  }
}

const REFRESH_PATH = '/api/v1/auth/refresh'
const LOGIN_PATH = '/api/v1/auth/login'
// Публичная best-effort ручка на анонимной форме — 401 не должен редиректить (DEV-1052).
const COMPANY_SEARCH_PATH = '/api/v1/agents/company-search'

// Отказы вкладки просмотра «глазами агента» (DEV-2400, ADR-0064). Тексты
// неизменны: их отдаёт общий интерцептор сервисов (pkg/interceptor в auth).
const IMPERSONATION_READ_ONLY = 'impersonation is read-only'
const IMPERSONATION_UNSUPPORTED = 'impersonation is not supported'
const IMPERSONATION_ENDED_PATH = '/impersonation-ended'
// Экран, где ходят пачкой запросов, не должен засыпать человека одинаковыми тостами.
const IMPERSONATION_TOAST_GAP_MS = 4000

// Точное совпадение пути (без query-строки), чтобы '/auth/login-history'
// и подобные суффиксы случайно не матчились.
const isPath = (url: string | undefined, path: string): boolean => {
  if (!url) {
    return false
  }
  const clean = url.split('?')[0] ?? ''
  return clean === path || clean.endsWith(path)
}

export function setupAuthInterceptor(axiosInstance: AxiosInstance) {
  // «Голый» клиент только под refresh: без request/response интерсепторов,
  // поэтому не несёт протухший Authorization и не зацикливается на своём 401.
  const refreshClient = axios.create({
    baseURL: axiosInstance.defaults.baseURL,
    timeout: axiosInstance.defaults.timeout,
  })

  // Один общий refresh «в полёте»: шквал параллельных 401 при загрузке страницы
  // (/me, /settings, …) не плодит гонку refresh-запросов.
  let refreshPromise: Promise<string> | null = null
  // Защита от многократного редиректа при шквале 401.
  let isRedirecting = false

  const redirectToLogin = () => {
    // Во вкладке просмотра мёртвый токен — конец сеанса просмотра: на форму
    // входа агента вести незачем (DEV-2400).
    const endedImpersonation = isImpersonating()
    tokenStorage.clear()

    if (isRedirecting) {
      return
    }
    isRedirecting = true

    if (endedImpersonation) {
      window.location.href = IMPERSONATION_ENDED_PATH
      return
    }

    // Полная перезагрузка на /login гарантированно сбрасывает всё состояние
    // мёртвой сессии. Отдельный агентский хост — вход всегда в корне. DEV-232/DEV-1014.
    window.location.href = '/login'
  }

  const persistTokens = (tokens: AuthTokens) => {
    tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken)

    // Синхронизируем реактивный Pinia-стор (его accessToken читают напрямую,
    // напр. presentation-download). Динамический импорт — чтобы не создавать
    // статическую циклическую зависимость api ↔ store.
    void import('@/features/auth/stores/auth.store')
      .then(({ useAuthStore }) => {
        useAuthStore().setTokens(tokens.accessToken, tokens.refreshToken)
      })
      .catch(() => {
        /* стор ещё не поднят — localStorage уже обновлён, этого достаточно */
      })
  }

  // Single-flight: возвращает новый accessToken либо реджектит, если refresh не удался.
  const refreshTokens = (): Promise<string> => {
    if (!refreshPromise) {
      refreshPromise = (async () => {
        const refreshToken = tokenStorage.getRefreshToken()
        if (!refreshToken) {
          throw new Error('No refresh token available')
        }

        // Refresh-токен передаётся в отдельном заголовке Refresh-Token (бэкенд
        // читает именно его — см. auth/refresh.go). Тело не используется.
        const { data } = await refreshClient.post<RefreshTokenResponse>(REFRESH_PATH, null, {
          headers: { 'Refresh-Token': `Bearer ${refreshToken}` },
        })

        const tokens = data?.tokens
        if (!tokens?.accessToken || !tokens?.refreshToken) {
          throw new Error('Malformed refresh response')
        }

        persistTokens(tokens)
        return tokens.accessToken
      })().finally(() => {
        refreshPromise = null
      })
    }

    return refreshPromise
  }

  const lastImpersonationToast: Record<string, number> = {}

  /** Отказ режима просмотра — тост вместо немого 403 на экране. */
  const handleImpersonationDenied = (error: AxiosError): void => {
    const message = (error.response?.data as { message?: unknown } | undefined)?.message
    if (typeof message !== 'string') return

    const key = message.includes(IMPERSONATION_READ_ONLY)
      ? 'impersonation.readOnlyToast'
      : message.includes(IMPERSONATION_UNSUPPORTED)
        ? 'impersonation.unsupportedToast'
        : null
    if (!key) return

    const now = Date.now()
    if (now - (lastImpersonationToast[key] ?? 0) > IMPERSONATION_TOAST_GAP_MS) {
      lastImpersonationToast[key] = now
      useToast().warning(i18n.global.t(key))
    }
  }

  // Request interceptor — добавление токена
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const token = tokenStorage.getAccessToken()

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }

      return config
    },
    (error) => Promise.reject(error),
  )

  // Response interceptor — обработка 401 и refresh
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config

      if (error.response?.status === 403 && isImpersonating()) {
        handleImpersonationDenied(error)
        return Promise.reject(error)
      }

      if (error.response?.status !== 401 || !originalRequest) {
        return Promise.reject(error)
      }

      // У вкладки просмотра refresh нет: 401 значит, что сеанс завершён (DEV-2400).
      if (isImpersonating()) {
        redirectToLogin()
        return Promise.reject(error)
      }

      // 401 на login — это неверные креды; пусть форма входа сама покажет ошибку.
      if (isPath(originalRequest.url, LOGIN_PATH)) {
        return Promise.reject(error)
      }

      // Публичные best-effort ручки на анонимных формах (подсказки компаний в регистрации
      // агента, DEV-1052): 401 не должен выкидывать пользователя с формы — отдаём ошибку
      // наверх, вызывающий гасит её сам (не редиректим, не рефрешим).
      if (isPath(originalRequest.url, COMPANY_SEARCH_PATH)) {
        return Promise.reject(error)
      }

      // Защитная ветка: 401 самого refresh (если его дёрнули через этот инстанс) —
      // сессия мертва, не зацикливаемся.
      if (isPath(originalRequest.url, REFRESH_PATH)) {
        redirectToLogin()
        return Promise.reject(error)
      }

      // Этот запрос уже ретраили после успешного refresh, а он опять 401 — НЕ
      // выкидываем принудительно из сессии (могла быть частная причина), просто
      // отдаём ошибку наверх.
      if (originalRequest._retry) {
        return Promise.reject(error)
      }
      originalRequest._retry = true

      try {
        const newAccessToken = await refreshTokens()

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        }

        return axiosInstance(originalRequest)
      } catch (refreshError) {
        // Refresh не удался — чистим токены и уводим на login (без бесконечных запросов).
        redirectToLogin()
        return Promise.reject(refreshError)
      }
    },
  )
}
